import {
    usePaginatedResource,
    useResourceItem,
    useCreateResource,
    useEditResource,
    useDeleteResource,
} from "./useCrudResource"
import {
    getQuotationStatuses,
    getQuotationStatus,
    createQuotationStatus,
    editQuotationStatus,
    deleteQuotationStatus,
    getAllQuotationStatuses,
    getTrashedQuotationStatuses,
    restoreQuotationStatus,
} from "api/quotationStatusApi"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const QUOTATION_STATUS_QUERY_KEY = "quotation-statuses"

// -------------------- FETCH PAGINATED --------------------
export const useQuotationStatuses = (params = {}, enabled = true) => {
  return usePaginatedResource(
    QUOTATION_STATUS_QUERY_KEY,
    getQuotationStatuses,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useQuotationStatus = (id, enabled = true) => {
  return useResourceItem(
    QUOTATION_STATUS_QUERY_KEY,
    getQuotationStatus,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateQuotationStatus = () => {
  return useCreateResource(createQuotationStatus, QUOTATION_STATUS_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditQuotationStatus = () => {
  return useEditResource(editQuotationStatus, QUOTATION_STATUS_QUERY_KEY)
}

// -------------------- DELETE --------------------
export const useDeleteQuotationStatus = () => {
  return useDeleteResource(deleteQuotationStatus, QUOTATION_STATUS_QUERY_KEY)
}

// -------------------- ADDITIONAL --------------------

// Get all quotation statuses without pagination
export const useAllQuotationStatuses = (params = {}) => {
  return usePaginatedResource(
    QUOTATION_STATUS_QUERY_KEY,
    getAllQuotationStatuses,
    { ...params, all: true },
    true
  )
}

// Get trashed quotation statuses
export const useTrashedQuotationStatuses = (params = {}) => {
  return usePaginatedResource(
    QUOTATION_STATUS_QUERY_KEY,
    getTrashedQuotationStatuses,
    params,
    true
  )
}

// Restore a trashed quotation status
export const useRestoreQuotationStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreQuotationStatus,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Update paginated cache
      queryClient.setQueriesData([QUOTATION_STATUS_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual item cache
      queryClient.setQueryData([QUOTATION_STATUS_QUERY_KEY, id], restoredItem)
    },
  })
}
